/**
 * Centralized Animations for Plan Payment Components
 * Requirements: 11.1, 11.2, 11.3, 11.4, 11.5
 * 
 * This file contains reusable Angular animations for the plan-payment module.
 * All animations respect prefers-reduced-motion for accessibility.
 */

import { 
  trigger, 
  transition, 
  style, 
  animate, 
  query, 
  stagger,
  AnimationTriggerMetadata 
} from '@angular/animations';

/**
 * Fade-in animation for cards and list items
 * Requirement 11.1: Fade-in for lists
 * Duration: 250ms (Requirement 11.2)
 */
export const fadeIn: AnimationTriggerMetadata = trigger('fadeIn', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(10px)' }),
    animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
  ])
]);

/**
 * Fade-in animation for modals
 * Requirement 11.4: Modal entrance animations
 * Duration: 250ms (Requirement 11.2)
 */
export const modalFadeIn: AnimationTriggerMetadata = trigger('modalFadeIn', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('250ms ease-out', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    animate('200ms ease-in', style({ opacity: 0 }))
  ])
]);

/**
 * Staggered list animation for multiple items
 * Requirement 11.1: Fade-in for lists with stagger effect
 * Duration: 250ms base + 50ms stagger (Requirement 11.2)
 */
export const listStagger: AnimationTriggerMetadata = trigger('listStagger', [
  transition('* => *', [
    query(':enter', [
      style({ opacity: 0, transform: 'translateY(10px)' }),
      stagger('50ms', [
        animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ], { optional: true })
  ])
]);

/**
 * Slide animation for tab transitions
 * Requirement 11.2: Tab transitions (250ms)
 */
export const slideInOut: AnimationTriggerMetadata = trigger('slideInOut', [
  transition(':enter', [
    style({ transform: 'translateX(100%)', opacity: 0 }),
    animate('250ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
  ]),
  transition(':leave', [
    animate('250ms ease-in', style({ transform: 'translateX(-100%)', opacity: 0 }))
  ])
]);

/**
 * Scale animation for hover effects (used in SCSS)
 * Requirement 11.3: Hover animations on cards
 * Note: This is primarily handled in SCSS, but can be used programmatically
 */
export const scaleOnHover: AnimationTriggerMetadata = trigger('scaleOnHover', [
  transition('normal => hover', [
    animate('200ms ease-out', style({ transform: 'scale(1.02)' }))
  ]),
  transition('hover => normal', [
    animate('200ms ease-in', style({ transform: 'scale(1)' }))
  ])
]);

/**
 * Expand/collapse animation for expandable sections
 * Requirement 11.1: Smooth transitions for UI elements
 */
export const expandCollapse: AnimationTriggerMetadata = trigger('expandCollapse', [
  transition(':enter', [
    style({ height: 0, opacity: 0, overflow: 'hidden' }),
    animate('250ms ease-out', style({ height: '*', opacity: 1 }))
  ]),
  transition(':leave', [
    style({ overflow: 'hidden' }),
    animate('200ms ease-in', style({ height: 0, opacity: 0 }))
  ])
]);

/**
 * Slide down animation for dropdowns and alerts
 * Requirement 11.1: Smooth UI transitions
 */
export const slideDown: AnimationTriggerMetadata = trigger('slideDown', [
  transition(':enter', [
    style({ height: 0, opacity: 0, overflow: 'hidden' }),
    animate('250ms ease-out', style({ height: '*', opacity: 1 }))
  ]),
  transition(':leave', [
    style({ overflow: 'hidden' }),
    animate('200ms ease-in', style({ height: 0, opacity: 0 }))
  ])
]);

/**
 * Bounce animation for attention-grabbing elements
 * Requirement 11.1: Visual feedback animations
 */
export const bounce: AnimationTriggerMetadata = trigger('bounce', [
  transition('* => bounce', [
    animate('300ms', style({ transform: 'translateY(-10px)' })),
    animate('300ms', style({ transform: 'translateY(0)' }))
  ])
]);

/**
 * Utility function to check if user prefers reduced motion
 * Requirement 11.5: Respect prefers-reduced-motion
 * 
 * Usage: Call this before triggering animations programmatically
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  return mediaQuery.matches;
}

/**
 * Get animation duration based on user preference
 * Requirement 11.5: Respect prefers-reduced-motion
 * 
 * @param normalDuration - Duration in ms for normal motion
 * @returns Duration in ms (0 if reduced motion preferred)
 */
export function getAnimationDuration(normalDuration: number): number {
  return prefersReducedMotion() ? 0 : normalDuration;
}

/**
 * Animation configuration constants
 * Requirement 11.2: Consistent animation timing
 */
export const ANIMATION_DURATIONS = {
  FAST: 150,
  NORMAL: 250,
  SLOW: 350,
  TAB_TRANSITION: 250,
  MODAL_FADE: 250,
  HOVER: 200
} as const;

/**
 * Easing functions
 * Requirement 11.1: Smooth, natural animations
 */
export const ANIMATION_EASINGS = {
  EASE_IN: 'ease-in',
  EASE_OUT: 'ease-out',
  EASE_IN_OUT: 'ease-in-out',
  LINEAR: 'linear'
} as const;
